import { forwardRef, useState, useCallback } from "react";
import TextareaAutosize from "react-textarea-autosize";
import { Mention, MentionsInput } from "react-mentions";
import SVG from "react-inlinesvg";
import { cn } from "src/lib/cn";
import { WithReset } from "src/sudokit/Layout/BoxReset";
import { Text } from "../Text";
import MenuSelect from "../SudoSelect/MenuSelect";
import IconButton from "src/components/IconButton";
import { useProjectStore } from "src/stores/projectStore";
import { useStoryBibleStore } from "src/stores/storyBibleStore";
import { useStructuredOutlineStore } from "src/components/StoryBible/StructuredOutline/StructuredOutlineStore";
import { MentionSuggestionsContainer } from "src/components/PersistentChat/mentions/MentionSuggestionsContainer";
import "src/components/PersistentChat/mentions/ReactMentionTextarea.css";

/**
 * @typedef {{
 *   ref: React.RefObject<HTMLTextAreaElement>,
 *   children?: React.ReactNode,
 *   mode: "default" | "ghost"
 *   bottomReveleadButtons?: React.ReactNode,
 *   leftActiveText?: React.ReactNode,
 *   rightActiveText?: React.ReactNode,
 *   leftActiveTextClassName?: string,
 *   rightActiveTextClassName?: string,
 *   minRows?: number,
 *   short?: boolean
 *   containerClassName?: string,
 *   alwaysShowControls?: boolean,
 *   enableMentions?: boolean,
 *   onPasteLongText?: (text: string) => void,
 *   error?: boolean,
 *   customMenuComponent?: React.ReactNode,
 *   selectProps?: {
 *     options: Array<{value: string, label: string, subLabel?: string, inlineSubLabel?: string}>,
 *     value?: string,
 *     onChange?: (value: string) => void,
 *     label: string,
 *     placeholder?: string,
 *     variant?: 'primary' | 'secondary' | 'tertiary' | 'secondaryGrey' | 'tertiaryGrey' | 'translucent',
 *     disabled?: boolean,
 *     className?: string,
 *     menuType?: 'button' | 'radiobutton',
 *     size?: 'default' | 'small',
 *     menuSide?: 'top' | 'bottom' | 'left' | 'right',
 *     menuAlign?: 'start' | 'center' | 'end',
 *     sideOffset?: number,
 *     alignOffset?: number
 *   },
 *   iconButtonProps?: {
 *     src: string,
 *     onClick?: () => void,
 *     disabled?: boolean,
 *     ariaLabel?: string,
 *     hoverTipText?: string,
 *     size?: number,
 *     iconSize?: number,
 *     variant?: 'primary' | 'secondary',
 *     className?: string
 *   },
 *   onSuggestionsVisibilityChange?: (visible: boolean) => void
 * }} BaseTextareaProps
 * */

// Mention styles for react-mentions integration
const mentionStyles = {
  control: {
    fontSize: "inherit",
    fontWeight: "normal",
  },
  highlighter: {
    overflow: "hidden",
    boxSizing: "border-box",
  },
  input: {
    margin: 0,
    overflow: "auto",
    color: "var(--grey-0)",
  },
  suggestions: {
    background: "transparent",
    paddingRight: 8,
    list: {
      fontSize: 14,
      overflow: "auto",
      maxHeight: "380px",
    },
    item: {
      padding: "9.5px 12px",
      borderRadius: "8px",
      "&focused": {
        backgroundColor: "var(--menu-color-hover)",
      },
      transition: "all 0.1s ease-in-out",
    },
  },
};

export const Textarea = forwardRef(
  /**
   * @param {BaseTextareaProps & import('react-textarea-autosize').TextareaAutosizeProps} props
   * @return {import("react").ReactElement}
   */
  ({ minRows, short, className, leftActiveText, rightActiveText, onFocus, onBlur, mode, containerClassName, leftActiveTextClassName, rightActiveTextClassName, customMenuComponent, selectProps, iconButtonProps, alwaysShowControls, enableMentions, onPasteLongText, error, value, onSuggestionsVisibilityChange, ...rest }, ref) => {
    const [focused, setFocused] = useState(false);

    // Mention-related hooks (only used when enableMentions is true)
    const characters = useStoryBibleStore((state) => state.charactersEntities);
    const worldbuilding = useStoryBibleStore((state) => state.worldbuildingEntries);
    const documents = useProjectStore((state) => state.contents);
    const isStoryBibleActive = useStoryBibleStore((state) => state.isActive);
    const outlineItems = useStructuredOutlineStore((state) => state.outline?.content?.items);
    const hasOutline = outlineItems && outlineItems.length > 0;

    const hasBottomText = !!leftActiveText || !!rightActiveText;
    const hasControls = !!selectProps || !!iconButtonProps || !!customMenuComponent;
    const hasBottomContent = hasBottomText || hasControls;

    // Mention search function
    const searchMentions = useCallback(
      (query, callback) => {
        if (!enableMentions) return callback([]);

        const searchQuery = query.toLowerCase();

        // Outline is a special pinned item (only one per project)
        const outlineItem = hasOutline && "outline".includes(searchQuery)
          ? [{
              id: "outline::current",
              display: "Outline",
              type: "outline",
            }]
          : [];

        const characterItems = (characters || [])
          .filter((char) => {
            const name = (char.displayName || "")?.toLowerCase?.();
            return name.includes(searchQuery);
          })
          .map((char) => ({
            id: `character::${char.id}`,
            display: char.displayName || "Unnamed Character",
            type: "character",
          }));

        const worldbuildingItems = (worldbuilding || [])
          .filter((entry) => {
            const name = (entry.displayName || "")?.toLowerCase?.();
            return name.includes(searchQuery);
          })
          .map((entry) => ({
            id: `worldbuilding::${entry.id}`,
            display: entry.displayName || "Unnamed Entry",
            type: "worldbuilding",
          }));

        const documentItems = (documents || [])
          .filter((doc) => {
            const title = (doc.title || "")?.toLowerCase?.();
            return title.includes(searchQuery);
          })
          .map((doc) => ({
            id: `document::${doc.id}`,
            display: doc.title || "Untitled Document",
            type: "document",
          }));

        if (isStoryBibleActive) {
          const results = [...outlineItem, ...characterItems, ...worldbuildingItems, ...documentItems].slice(0, 8);
          callback(results);
        } else {
          const results = [...outlineItem, ...documentItems].slice(0, 8);
          callback(results);
        }
      },
      [characters, worldbuilding, documents, isStoryBibleActive, enableMentions, hasOutline],
    );

    // Mention suggestion renderer
    const renderSuggestion = useCallback(
      (suggestion, _, highlightedDisplay) => {
        const iconSrc = {
          character: "/assets/character-icon.svg",
          worldbuilding: "/assets/world.svg",
          document: "/assets/document.svg",
          outline: "/assets/story-bible/outline-icon.svg",
        }[suggestion.type] || "/assets/document.svg";

        return (
          <WithReset className={cn("flex items-center gap-2 cursor-pointer transition-all w-[16rem]")}>
            <WithReset className={cn("w-6 h-6 rounded-full flex items-center justify-center shrink-0")}>
              <SVG
                src={iconSrc}
                className="w-5 h-5 [&_path]:fill-app-grey-0"
              />
            </WithReset>
            <Text className="truncate">{highlightedDisplay}</Text>
          </WithReset>
        );
      },
      [],
    );

    // Handle mention changes
    const handleMentionChange = useCallback(
      (_, newValue) => {
        if (rest.onChange) {
          // Create synthetic event for compatibility
          const syntheticEvent = {
            target: { value: newValue },
            currentTarget: { value: newValue },
          };
          rest.onChange(syntheticEvent);
        }
      },
      [rest.onChange],
    );

    // Handle paste events for long text
    const handlePaste = useCallback((e) => {
      if (onPasteLongText) {
        const text = e.clipboardData.getData("text/plain");
        if (text.length >= 300) {
          e.preventDefault();
          e.stopPropagation();
          onPasteLongText(text);
          return;
        }
      }
      // Allow normal paste behavior
      if (rest.onPaste) {
        rest.onPaste(e);
      }
    }, [onPasteLongText, rest.onPaste]);

    return (
      <WithReset className={cn("relative", containerClassName)}>
        {enableMentions ? (
          // Mentions-enabled textarea using react-mentions
          <WithReset
            className={cn(
              "w-full rounded-[5px] overflow-hidden",
              error
                ? "border-[1.5px] border-app-danger-color focus-within:border-app-danger-color hover:border-app-danger-color"
                : mode === "ghost"
                ? "border border-transparent hover:border-app-grey-3 hover:shadow-tiny focus-within:border-app-primary-cta-high-contrast hover:focus-within:border-app-primary-cta"
                : "border border-app-grey-3 focus-within:border-app-primary-cta-high-contrast focus-within:shadow-tiny",
              "transition-[border,box-shadow] duration-100 ease-in-out",
            )}
            onPaste={handlePaste}
          >
            <MentionsInput
              value={value || ""}
              onChange={handleMentionChange}
              onFocus={(e) => {
                setFocused(true);
                onFocus?.(e);
              }}
              onBlur={(e) => {
                setFocused(false);
                onBlur?.(e);
              }}
              placeholder={rest.placeholder}
              inputRef={ref}
              style={{
                ...mentionStyles,
                control: {
                  ...mentionStyles.control,
                  minHeight: !short ? "120px" : "auto",
                },
                input: {
                  ...mentionStyles.input,
                  padding: hasBottomContent ? (hasControls ? "0.6rem 12px 3.2rem 8px" : "0.6rem 8px 2rem 8px") : "0.6rem 8px",
                  minHeight: !short ? "120px" : "auto",
                  fontFamily: "Inter, system-ui, sans-serif",
                  fontSize: "inherit",
                  lineHeight: "1.4",
                  resize: "none",
                },
                highlighter: {
                  ...mentionStyles.highlighter,
                  padding: hasBottomContent ? (hasControls ? "0.6rem 8px 3.2rem 8px" : "0.6rem 8px 2rem 8px") : "0.6rem 8px",
                },
              }}
              className={cn("mentions-input", className)}
              suggestionsPortalHost={document.body}
              a11ySuggestionsListLabel="Mention suggestions"
              forceSuggestionsAboveCursor
              onKeyDown={rest.onKeyDown}
              customSuggestionsContainer={(children) => (
                <MentionSuggestionsContainer onVisibilityChange={onSuggestionsVisibilityChange}>
                  {children}
                </MentionSuggestionsContainer>
              )}
            >
              <Mention
                trigger="~~pasted~~"
                data={[]}
                renderSuggestion={() => null}
                displayTransform={(_, display) => `Pasted ${display} words`}
                markup="~~pasted~~[__display__](__id__)"
                appendSpaceOnAdd
                style={{
                  backgroundColor: "var(--primary-cta-transparent-15)",
                  borderRadius: "4px",
                }}
              />
              <Mention
                trigger="#"
                data={searchMentions}
                renderSuggestion={renderSuggestion}
                displayTransform={(_, display) => `#${display}`}
                markup="#[__display__](__id__)"
                appendSpaceOnAdd
                style={{
                  backgroundColor: "var(--primary-cta-transparent-15)",
                  borderRadius: "4px",
                }}
              />
            </MentionsInput>
          </WithReset>
        ) : (
          // Regular textarea without mentions
          <TextareaAutosize
            ref={ref}
            minRows={minRows || 1}
            className={cn(
              "w-full max-w-full resize-none rounded-[5px] font-inter text-app-grey-0 transition-[border,box-shadow] duration-100 ease-in-out bg-transparent leading-[1.4] px-2 pt-[0.6rem]",
              "placeholder:text-app-grey-1 overflow-hidden",
              hasBottomContent ? (hasControls ? "pb-[2.8rem]" : "pb-[1.6rem]") : "pb-[0.6rem]",
              error
                ? "border-[1.5px] border-app-danger-color focus:border-app-danger-color hover:border-app-danger-color"
                : mode === "ghost"
                ? "border border-transparent hover:border-app-grey-3 hover:shadow-tiny focus:border-app-primary-cta-high-contrast hover:focus:border-app-primary-cta"
                : "border border-app-grey-3 focus:border-app-primary-cta-high-contrast focus:shadow-tiny",
              !short && "min-h-[120px]",
              className
            )}
            onFocus={(e) => {
              setFocused(true);
              onFocus?.(e);
            }}
            onBlur={(e) => {
              setFocused(false);
              onBlur?.(e);
            }}
            onPaste={handlePaste}
            value={value || ""}
            {...rest}
          />
        )}

        {/* Bottom content area */}
        {hasBottomContent && (
          <div
            className={cn(
              "flex absolute left-0 right-0 bottom-0 px-[0.6rem] pb-[0.45rem] transition-[opacity,transform] duration-100",
              (alwaysShowControls && hasControls) || focused
                ? "opacity-100 pointer-events-auto translate-y-0"
                : "opacity-0 pointer-events-none translate-y-1"
            )}
          >
            {/* Left side: Text or Select */}
            <div className="flex items-center flex-1">
              {customMenuComponent}
              {!customMenuComponent && selectProps && (
                <MenuSelect
                  options={selectProps.options}
                  value={selectProps.value}
                  onChange={selectProps.onChange}
                  label={selectProps.label}
                  placeholder={selectProps.placeholder}
                  variant={selectProps.variant || "tertiary"}
                  disabled={selectProps.disabled}
                  menuType={selectProps.menuType || "button"}
                  size={selectProps.size || "default"}
                  menuSide={selectProps.menuSide || "bottom"}
                  menuAlign={selectProps.menuAlign || "center"}
                  sideOffset={selectProps.sideOffset || 0}
                  alignOffset={selectProps.alignOffset || 0}
                  className={cn("text-xs h-8", selectProps.className)}
                />
              )}
              {!customMenuComponent && !selectProps && leftActiveText && (
                <span
                  aria-readonly="true"
                  className={cn("text-xs font-medium text-app-grey-1", leftActiveTextClassName)}
                >
                  {leftActiveText}
                </span>
              )}
            </div>

            {/* Center: Text if no controls */}
            {!selectProps && !iconButtonProps && rightActiveText && !leftActiveText && (
              <div className="flex items-center justify-end flex-1">
                <span
                  aria-readonly="true"
                  className={cn("text-xs font-medium text-app-grey-1", rightActiveTextClassName)}
                >
                  {rightActiveText}
                </span>
              </div>
            )}

            {/* Right side: Text or IconButton */}
            <div className="flex items-center">
              {iconButtonProps && (
                <IconButton
                  src={iconButtonProps.src}
                  onClick={iconButtonProps.onClick}
                  disabled={iconButtonProps.disabled}
                  ariaLabel={iconButtonProps.ariaLabel}
                  hoverTipText={iconButtonProps.hoverTipText}
                  size={iconButtonProps.size || 32}
                  iconSize={iconButtonProps.iconSize || 24}
                  variant={iconButtonProps.variant || "secondary"}
                  className={cn("ml-2", iconButtonProps.className)}
                />
              )}
              {!iconButtonProps && rightActiveText && (selectProps || leftActiveText) && (
                <span
                  aria-readonly="true"
                  className={cn("text-xs font-medium text-app-grey-1", rightActiveTextClassName)}
                >
                  {rightActiveText}
                </span>
              )}
            </div>
          </div>
        )}
      </WithReset>
    );
  }
);
